import {
  differenceInCalendarDays,
  format,
  formatDistanceToNowStrict,
  parseISO,
} from 'date-fns';

import { communicationPreferenceOptions } from '@ownhealthil/urine-enums';

import { reporter } from '@packages/reporter';
import { formatMaterialTableDate, preParse } from 'utils/dates';

import { FILTER_LABELS } from './constants';

export function dateFormat(date) {
  return formatMaterialTableDate(date);
}

export function timeFormat(date) {
  return format(date, 'HH:mm:ss');
}

export function dateTimeFormat({ date, time24Hour }) {
  const clockFormat = time24Hour ? 'HH:mm' : 'hh:mm a';

  return format(preParse(date), `d MMM yyyy, ${clockFormat}`);
}

/**
 * Return the distance between the given date and now in words.
 *
 * @see
 * https://date-fns.org/docs/formatDistanceToNowStrict
 */
export function distanceToNow(date) {
  return formatDistanceToNowStrict(parseISO(date));
}

/**
 * Return the number of days between a given date and now.
 *
 * @see
 * https://date-fns.org/docs/differenceInDays
 */
export function differenceInDaysToNow(date) {
  return differenceInCalendarDays(new Date(), parseISO(date));
}

/** Compare an object with another and get the differences in properties by values. */
export function checkObjectDiff(object1, object2) {
  const keysChanged = [];

  Object.keys(object1).forEach((key) => {
    if (object2?.[key] !== undefined) {
      if (object1[key] !== object2[key]) {
        keysChanged.push(key);
      }
    }
  });

  return keysChanged;
}

/**
 * Capture an exception event and send it to Sentry along with extra metadata.
 *
 * We don't report 401 and 500 server errors.
 * 401 is usually associated with an expired token, which is a part of a valid flow.
 *
 * @param {*} exception - An exception-like object
 * @param {object} metadata - Extra metadata to be set with the report
 */
export function reportError(exception, metadata) {
  if ([401, 500].includes(metadata?.error?.meta?.statusCode)) {
    return;
  }

  reporter.error(exception, metadata);
}

/**
 * Get the back links for a page and transform them to a format that can be used by the Breadcrumbs component.
 * @param {*} backUrls
 * @returns {Array} An array of objects with the following shape: { to: string, label: string }
 */
export function transformBackUrlsToLinks(backUrls) {
  const URL_LABELS = {
    '/calls': 'Calls Queue',
    '/patient-list': 'Patient List',
    '/pcp-center': 'PCP Center',
    '/partner-settings': 'Partner Settings',
    '/import-patients': 'Import Patients',
  };

  return backUrls.map((backUrl) => ({
    to: backUrl,
    label: URL_LABELS[backUrl] ?? 'Patient page', // ** If there is no label for the URL, use the default label ** //
  }));
}

export function isMobile() {
  const agent = navigator.userAgent ?? navigator.vendor ?? window.opera;
  const MOBILE_REGEX =
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i;

  if (MOBILE_REGEX.test(agent)) {
    return true;
  }

  return false;
}

/**
 * Prepare an Object that needs to be sent to the backend as a payload.
 * - Trim String values.
 * - Change empty Strings into null.
 */
export function toPayload(payload) {
  const replacer = (key, value) => {
    if (typeof value === 'string') {
      const hasValue = Boolean(value.trim());

      return hasValue ? value.trim() : null;
    }

    return value;
  };

  return JSON.parse(JSON.stringify(payload, replacer));
}

/**
 * Extract fields recursively from a form object by using a set of field keys.
 *
 * @example
 * getChangedFields({ a: 'A', b: 'B' }, { a: true }) // Returns { a: 'A' }
 *
 * @param {object} allValues
 * @param {(object|boolean)} fields
 * @returns {object}
 */
export function getChangedFields(allValues, fields) {
  if (fields === true) {
    return allValues;
  }

  return Object.fromEntries(
    Object.keys(fields).map((key) => [
      key,
      getChangedFields(allValues[key], fields[key]),
    ]),
  );
}

/**
 * Parse full address of patient or PCP.
 *
 * @param {object} address
 * @returns {string}
 */
export function parseFullAddress(address, addressType = 'patient') {
  if (!address || Object.values(address).length === 0) return null;

  return [
    address?.addressLine1,
    address?.addressLine2,
    address?.city,
    address?.country,
    address?.stateOrProvince,
    ...(addressType === 'patient' ? [address?.postalCode] : [address?.zipCode]),
  ]
    .filter((value) => value)
    .join(', ');
}

/**
 * Check if a filters object has active filters in it.
 *
 * @return {bool}
 */
export function areFiltersActive(filters) {
  return Object.values(filters).some((filterGroup) => {
    switch (filterGroup.type) {
      case 'range':
        return Boolean(filterGroup.filters.min || filterGroup.filters.max);

      case 'multi':
        return Boolean(filterGroup.filters.length);

      default:
        return false;
    }
  });
}

/**
 * A utility that takes an array and a value and either adds or removes it from the array.
 *
 * @param {string[]} array
 * @param {string} value
 * @return {array}
 */
export function toggleArray({ array, value }) {
  const newArray = [...array];
  const notInArray = newArray.indexOf(value) === -1;

  if (notInArray) {
    newArray.push(value);
  } else {
    newArray.splice(newArray.indexOf(value), 1);
  }

  return newArray;
}

/**
 * Shapes filters with key and label
 *
 * @example
 * shapeFilters({ language: ['kor'] }) // Returns { language: { key: 'kor', label: 'Korean' }}
 *
 * @param {Object<string, Array<string>>} filters
 * @returns {Object<string, Array<{ key: string, label: string }>>}
 */
export function shapeFilters(filters) {
  const shapedFilters = {};

  Object.entries(filters).forEach(([filterName, filterKeys]) => {
    shapedFilters[filterName] = filterKeys.map((key) => ({
      key,
      label: FILTER_LABELS?.[filterName]?.[key] ?? key,
    }));
  });

  return shapedFilters;
}

/**
 * Strip partner information from an email address.
 *
 * @example
 * 'john.doe+partner@healthy.io' = 'john.doe@healthy.io'
 *
 * @param {string} email - User's email addresses associated with a partner
 * @returns {string} Email address without a partner name
 */
export function shapeUserEmail(email) {
  return email?.replace(/\+.*(?=@)/, '').toLowerCase();
}

export function getModifiedFieldsForTracking(modifiedFields) {
  const fieldsToTrack = {};
  Object.entries(modifiedFields).forEach(([key, value]) => {
    if (key === 'address') {
      Object.entries(value).forEach(([addressLine, addressLineValue]) => {
        fieldsToTrack[addressLine] = addressLineValue?.length ?? 0;
      });
    } else {
      fieldsToTrack[key] = value?.length ?? 0;
    }
  });
  return fieldsToTrack;
}

export function isOutreachAllowed({
  outreachType,
  patientCommunicationPreferences,
  partnerCommunicationPreferences,
}) {
  const isAllowedByPatient =
    patientCommunicationPreferences?.[outreachType] ===
    communicationPreferenceOptions.ALLOWED;
  if (isAllowedByPatient) return true;

  const isDeniedByPatient =
    patientCommunicationPreferences?.[outreachType] ===
    communicationPreferenceOptions.DENIED;
  if (isDeniedByPatient) return false;

  /* Else, if the patient communication preference is not defined
    (patientCommunication?.[type] === communicationPreferenceOptions.DEFAULT),
    we should use the partner's configuration */
  const isAllowedByPartner =
    partnerCommunicationPreferences?.[outreachType] ===
    communicationPreferenceOptions.ALLOWED;

  return isAllowedByPartner;
}
